import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/layout';
import { ToastProvider } from './components/ui';
import { useAuthStore } from './stores/authStore';
import { useThemeStore } from './stores/themeStore';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PageSkeleton } from './components/LoadingSkeleton';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const EventDetailPage = lazy(() => import('./pages/EventDetailPage'));
const CreateEventPage = lazy(() => import('./pages/CreateEventPage'));
const NewsPage = lazy(() => import('./pages/NewsPage'));
const NewsDetailPage = lazy(() => import('./pages/NewsDetailPage'));
const CreateNewsPage = lazy(() => import('./pages/CreateNewsPage'));
const FeedPage = lazy(() => import('./pages/FeedPage'));
const SubgroupsPage = lazy(() => import('./pages/SubgroupsPage'));
const SubgroupDetailPage = lazy(() => import('./pages/SubgroupDetailPage'));
const CreateSubgroupPage = lazy(() => import('./pages/CreateSubgroupPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const MembersPage = lazy(() => import('./pages/MembersPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const PostDetailPage = lazy(() => import('./pages/PostDetailPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const HackathonsPage = lazy(() => import('./pages/HackathonsPage'));
const HackathonDetailPage = lazy(() => import('./pages/HackathonDetailPage'));
const FunPage = lazy(() => import('./pages/FunPage'));
const WrappedPage = lazy(() => import('./pages/WrappedPage'));
const TeamBuilderPage = lazy(() => import('./pages/TeamBuilderPage'));
const FeatureRequestsPage = lazy(() => import('./pages/FeatureRequestsPage'));
const ReadingListPage = lazy(() => import('./pages/ReadingListPage'));
const DiscoverPage = lazy(() => import('./pages/DiscoverPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const GroupsPage = lazy(() => import('./pages/GroupsPage'));
const GroupDetailPage = lazy(() => import('./pages/GroupDetailPage'));
const GodModePage = lazy(() => import('./pages/GodModePage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const CodeOfConductPage = lazy(() => import('./pages/CodeOfConductPage'));

// Auth pages
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const SignupPage = lazy(() => import('./pages/auth/SignupPage'));
const AuthCallbackPage = lazy(() => import('./pages/auth/AuthCallbackPage'));
const CompleteProfilePage = lazy(() => import('./pages/auth/CompleteProfilePage'));

// Create React Query client - aggressive refetching for fresh data
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0, // Always consider data stale
      gcTime: 1000 * 60 * 5, // 5 minutes garbage collection
      retry: 1,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    },
  },
});

// Loading spinner component with skeleton
const PageLoader: React.FC = () => (
  <div className="max-w-7xl mx-auto px-4 py-8">
    <PageSkeleton />
  </div>
);

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isInitialized, isLoading } = useAuthStore();

  // Wait for auth to initialize
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-green-500" />
      </div>
    );
  }

  // Initialized and no user - redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// 404 Page
const NotFoundPage: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-950">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-white mb-4">404</h1>
      <p className="text-slate-400 mb-8">Page not found</p>
      <a href="/" className="text-green-500 hover:text-green-400">
        Go back home
      </a>
    </div>
  </div>
);

// Full-page loading screen for auth initialization
const AuthLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-950">
    <div className="flex flex-col items-center gap-4">
      <div className="w-16 h-16 bg-slate-800 rounded-2xl animate-pulse" />
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
      <p className="text-slate-500 text-sm">Loading...</p>
    </div>
  </div>
);

// App content with auth initialization
const AppContent: React.FC = () => {
  const { isInitialized } = useAuthStore();
  const { theme } = useThemeStore();

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // Show loading while auth initializes - this should be very fast now
  if (!isInitialized) {
    return <AuthLoader />;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
        {/* Auth routes (no layout) */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route
          path="/complete-profile"
          element={
            <ProtectedRoute>
              <CompleteProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Main routes with layout */}
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          
          {/* Events */}
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:id" element={<EventDetailPage />} />
          <Route
            path="/events/create"
            element={
              <ProtectedRoute>
                <CreateEventPage />
              </ProtectedRoute>
            }
          />
          
          {/* News */}
          <Route path="/news" element={<NewsPage />} />
          <Route
            path="/news/create"
            element={
              <ProtectedRoute>
                <CreateNewsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/news/:slug" element={<NewsDetailPage />} />

          {/* Projects */}
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:slug" element={<ProjectDetailPage />} />

          {/* Hackathons */}
          <Route path="/hackathons" element={<HackathonsPage />} />
          <Route path="/hackathons/:slug" element={<HackathonDetailPage />} />

          {/* Fun */}
          <Route path="/fun" element={<FunPage />} />

          {/* Wrapped */}
          <Route
            path="/wrapped"
            element={
              <ProtectedRoute>
                <WrappedPage />
              </ProtectedRoute>
            }
          />
          
          {/* Feed */}
          <Route
            path="/feed"
            element={
              <ProtectedRoute>
                <FeedPage />
              </ProtectedRoute>
            }
          />
          <Route path="/feed/:id" element={<ProtectedRoute><PostDetailPage /></ProtectedRoute>} />
          
          {/* Subgroups */}
          <Route path="/subgroups" element={<SubgroupsPage />} />
          <Route path="/subgroups/create" element={<ProtectedRoute><CreateSubgroupPage /></ProtectedRoute>} />
          <Route path="/subgroups/:slug" element={<SubgroupDetailPage />} />
          
          {/* Leaderboard & Members */}
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/members" element={<MembersPage />} />
          <Route path="/discover" element={<DiscoverPage />} />
          
          {/* Team Builder */}
          <Route path="/team-builder" element={<TeamBuilderPage />} />
          
          {/* Feature Requests */}
          <Route path="/feature-requests" element={<FeatureRequestsPage />} />
          
          {/* Help */}
          <Route path="/help" element={<HelpPage />} />
          
          {/* Reading List */}
          <Route
            path="/saved"
            element={
              <ProtectedRoute>
                <ReadingListPage />
              </ProtectedRoute>
            }
          />
          
          {/* Messages */}
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            }
          />
          
          {/* Profile & Settings */}
          <Route path="/profile/:username" element={<ProfilePage />} />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          
          {/* Groups */}
          <Route path="/groups" element={<GroupsPage />} />
          <Route path="/groups/:id" element={<GroupDetailPage />} />
          
          
          {/* Admin */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          
          {/* God Mode (Super Admin) */}
          <Route
            path="/god-mode"
            element={
              <ProtectedRoute>
                <GodModePage />
              </ProtectedRoute>
            }
          />

          {/* Legal Pages */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/code-of-conduct" element={<CodeOfConductPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
};

// Main App component
const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
};

export default App;
